import React from 'react';
import { Button, Space, Typography } from 'antd';
import { ProjectOutlined, FilePdfOutlined, SearchOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const EmptyStateIllustrations = ({ 
  type = 'projects', // 'projects', 'documents', 'search', 'dashboard'
  title,
  description,
  primaryAction,
  secondaryActions = []
}) => {
  const renderProjectsIllustration = () => (
    <svg width="200" height="160" viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Background circles */}
      <circle cx="40" cy="40" r="25" fill="#f3f4f6" />
      <circle cx="160" cy="120" r="20" fill="#f3f4f6" />
      
      {/* Main folder */}
      <path d="M60 50 L60 120 L140 120 L140 70 L110 70 L100 50 L60 50 Z" fill="#e5e7eb" stroke="#9ca3af" strokeWidth="2"/>
      
      {/* Folder tab */}
      <path d="M100 50 L110 70 L140 70" fill="#d1d5db" stroke="#9ca3af" strokeWidth="2"/>
      
      {/* Document lines */}
      <rect x="75" y="80" width="50" height="3" fill="#9ca3af" rx="1"/>
      <rect x="75" y="88" width="40" height="3" fill="#9ca3af" rx="1"/>
      <rect x="75" y="96" width="45" height="3" fill="#9ca3af" rx="1"/>
      
      {/* Plus button */}
      <circle cx="120" cy="50" r="12" fill="#9333ea" />
      <path d="M115 50 L125 50 M120 45 L120 55" stroke="white" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );

  const renderDocumentsIllustration = () => (
    <svg width="200" height="160" viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Background elements */}
      <circle cx="30" cy="30" r="15" fill="#fef3c7" />
      <circle cx="170" cy="130" r="18" fill="#dbeafe" />
      
      {/* PDF documents */}
      <g transform="translate(60, 40)">
        {/* First PDF */}
        <rect x="0" y="0" width="35" height="45" fill="#ef4444" rx="2"/>
        <rect x="0" y="0" width="35" height="8" fill="#dc2626" rx="2"/>
        <text x="17.5" y="5" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">PDF</text>
        
        {/* Second PDF */}
        <rect x="45" y="10" width="35" height="45" fill="#3b82f6" rx="2"/>
        <rect x="45" y="10" width="35" height="8" fill="#2563eb" rx="2"/>
        <text x="62.5" y="15" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">PDF</text>
        
        {/* Third PDF */}
        <rect x="22" y="55" width="35" height="45" fill="#10b981" rx="2"/>
        <rect x="22" y="55" width="35" height="8" fill="#059669" rx="2"/>
        <text x="39.5" y="60" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">PDF</text>
      </g>
      
      {/* Upload arrow */}
      <g transform="translate(140, 80)">
        <circle cx="20" cy="20" r="18" fill="#f3f4f6" stroke="#9ca3af" strokeWidth="2"/>
        <path d="M20 12 L20 28 M12 20 L20 12 L28 20" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </g>
    </svg>
  );

  const renderSearchIllustration = () => (
    <svg width="200" height="160" viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Magnifying glass */}
      <circle cx="80" cy="70" r="25" fill="none" stroke="#d1d5db" strokeWidth="3"/>
      <circle cx="80" cy="70" r="18" fill="#f9fafb"/>
      <path d="M98 88 L130 120" stroke="#d1d5db" strokeWidth="3" strokeLinecap="round"/>
      
      {/* Search lines */}
      <path d="M65 65 Q80 60 95 65" stroke="#9333ea" strokeWidth="2" fill="none" opacity="0.6"/>
      <path d="M70 75 Q80 70 90 75" stroke="#9333ea" strokeWidth="2" fill="none" opacity="0.4"/>
      
      {/* Question marks */}
      <text x="40" y="40" fill="#9ca3af" fontSize="16" opacity="0.5">?</text>
      <text x="120" y="50" fill="#9ca3af" fontSize="14" opacity="0.4">?</text>
      <text x="50" y="100" fill="#9ca3af" fontSize="12" opacity="0.3">?</text>
      <text x="110" y="90" fill="#9ca3af" fontSize="10" opacity="0.3">?</text>
    </svg>
  );

  const renderDashboardIllustration = () => (
    <svg width="200" height="160" viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Chart bars */}
      <rect x="40" y="100" width="15" height="20" fill="#e5e7eb" rx="2"/>
      <rect x="60" y="80" width="15" height="40" fill="#d1d5db" rx="2"/>
      <rect x="80" y="60" width="15" height="60" fill="#9333ea" rx="2"/>
      <rect x="100" y="70" width="15" height="50" fill="#d1d5db" rx="2"/>
      <rect x="120" y="90" width="15" height="30" fill="#e5e7eb" rx="2"/>
      
      {/* Trend line */}
      <path d="M45 95 L67 75 L87 55 L107 65 L127 85" stroke="#10b981" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <circle cx="87" cy="55" r="3" fill="#10b981"/>
      
      {/* Grid lines */}
      <line x1="35" y1="120" x2="140" y2="120" stroke="#e5e7eb" strokeWidth="1"/>
      <line x1="35" y1="100" x2="140" y2="100" stroke="#f3f4f6" strokeWidth="1" strokeDasharray="2,2"/>
      <line x1="35" y1="80" x2="140" y2="80" stroke="#f3f4f6" strokeWidth="1" strokeDasharray="2,2"/>
      <line x1="35" y1="60" x2="140" y2="60" stroke="#f3f4f6" strokeWidth="1" strokeDasharray="2,2"/>
    </svg>
  );

  const getIllustration = () => {
    switch (type) {
      case 'projects':
        return renderProjectsIllustration();
      case 'documents':
        return renderDocumentsIllustration();
      case 'search':
        return renderSearchIllustration();
      case 'dashboard':
        return renderDashboardIllustration();
      default:
        return renderProjectsIllustration();
    }
  };

  const getDefaultTitle = () => {
    switch (type) {
      case 'projects':
        return 'No projects yet';
      case 'documents':
        return 'No documents found';
      case 'search':
        return 'No results found';
      case 'dashboard':
        return 'No data to display';
      default:
        return 'Nothing here yet';
    }
  };

  const getDefaultDescription = () => {
    switch (type) {
      case 'projects':
        return 'Create your first construction project to start organizing your plans and documents.';
      case 'documents':
        return 'Upload PDF documents to get started with document management and analysis.';
      case 'search':
        return 'Try adjusting your search terms or filters to find what you are looking for.';
      case 'dashboard':
        return 'Start by creating projects and uploading documents to see your analytics here.';
      default:
        return 'Get started by creating your first item.';
    }
  };

  return (
    <div style={{ 
      textAlign: 'center', 
      padding: '60px 20px',
      background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
      borderRadius: '12px',
      border: '1px solid #e5e5e5',
      margin: '20px 0'
    }}>
      <div style={{ marginBottom: '24px' }}>
        {getIllustration()}
      </div>
      
      <Title level={4} style={{ color: '#171717', marginBottom: '12px' }}>
        {title || getDefaultTitle()}
      </Title>
      
      <Text style={{ 
        color: '#737373', 
        fontSize: '14px',
        display: 'block',
        maxWidth: '400px',
        margin: '0 auto 32px',
        lineHeight: '1.5'
      }}>
        {description || getDefaultDescription()}
      </Text>
      
      {(primaryAction || secondaryActions.length > 0) && (
        <Space size="middle" wrap>
          {primaryAction && (
            <Button {...primaryAction.props}>
              {primaryAction.label}
            </Button>
          )}
          {secondaryActions.map((action, index) => (
            <Button key={index} {...action.props}>
              {action.label}
            </Button>
          ))}
        </Space>
      )}
    </div>
  );
};

export default EmptyStateIllustrations;
